import { Box, Stack, Typography } from "@mui/material";
import { memo } from "react";
import MultiProgress, { ProgressComponentProps } from "react-multi-progress";
import { APP_COLORS } from "../../enums";

const PROGRESS_HEIGHT = 18;
const BORDER_RADIUS = "6px";
const LABEL_INDICATOR_SIZE = 14;

function lightenColor(hex: string, amount = 0.35): string {
  const cleaned = hex.replace("#", "");
  const r = parseInt(cleaned.substring(0, 2), 16);
  const g = parseInt(cleaned.substring(2, 4), 16);
  const b = parseInt(cleaned.substring(4, 6), 16);
  const newR = Math.round(r + (255 - r) * amount);
  const newG = Math.round(g + (255 - g) * amount);
  const newB = Math.round(b + (255 - b) * amount);
  return `rgb(${newR}, ${newG}, ${newB})`;
}

const getStripePattern = (baseColor: string) => {
  const lightColor = lightenColor(baseColor);
  return `repeating-linear-gradient(135deg, ${baseColor} 0px, ${baseColor} 4px, ${lightColor} 4px, ${lightColor} 8px)`;
};

const StripedProgressElement = memo(function StripedProgressElement(
  props: ProgressComponentProps<Record<string, never>>
) {
  const { children, element, ...rest } = props;
  const restStyle = (rest as { style?: React.CSSProperties }).style;
  return (
    <div
      {...rest}
      style={{
        ...restStyle,
        background: getStripePattern(element.color),
        borderRadius: BORDER_RADIUS,
      }}
    >
      {children}
    </div>
  );
});

interface LabelItem {
  label: string;
  color: string;
  value: number;
}

const ProgressLabel = memo(function ProgressLabel({ item }: { item: LabelItem }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Box
        width={LABEL_INDICATOR_SIZE}
        height={LABEL_INDICATOR_SIZE}
        borderRadius={1}
        bgcolor={item.color}
      />
      <Typography variant="small-ibmPlexMono-medium">
        {item.label}
      </Typography>
      <Typography
        variant="small-ibmPlexMono"
        color={item.color}
        border={`1px solid ${item.color}`}
        borderRadius={BORDER_RADIUS}
        px={0.3}
      >
        {item.value}%
      </Typography>
    </Stack>
  );
});

interface ProgressElementInput {
  value: number;
  color: string;
}

interface MultiColorProgressbarProps {
  elements: ProgressElementInput[];
  withLabel?: boolean;
  labels?: LabelItem[];
}

function MultiColorProgressbar({
  elements,
  withLabel,
  labels,
}: MultiColorProgressbarProps) {
  return (
    <Stack gap={1}>
      <Box>
        <MultiProgress<object>
          elements={elements}
          height={PROGRESS_HEIGHT}
          round={false}
          component={StripedProgressElement}
        />
      </Box>
      {withLabel && labels?.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={2}>
          {labels.map((item, index) => (
            <ProgressLabel key={`${item.label}-${index}`} item={item} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

export { MultiColorProgressbar };
